/**
 * B-GTCC-FMT5 — locale-true formatters for the admin pages.
 *
 * Ten call sites across db-ops, gateway, invitations, purge and tenants read
 * their locale from the machine: `Date.prototype.toLocaleString()` and
 * `Number.prototype.toLocaleString()` without an argument follow the browser,
 * not the app. An operator who had switched the admin app to English still saw
 * `5.3.2026, 08:09:04` and `1.234.567` on a German workstation, next to a page
 * that was otherwise English.
 *
 * The locale comes from `activeLocale()` — the same source `monitoring/utils.ts`
 * and `data-plane/utils.ts` already use — and is a parameter with a default
 * rather than a hook, because most of these call sites sit inside table `cell`
 * lambdas and row components where a hook cannot run.
 *
 * The option shapes below reproduce the defaults of the calls they replace
 * field for field, so with the machine locale and the app locale in agreement
 * the rendered text is byte-identical to what it was. Measured for de, en, fr
 * and ar in `locale-format.test.ts`.
 */

import { activeLocale, type Locale } from "@genius-tracker/i18n";

/** The field set `Date.prototype.toLocaleString()` uses when given no options. */
const DATE_TIME_FIELDS = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
} as const;

/**
 * Date and time in the app's locale, replacing `new Date(x).toLocaleString()`.
 *
 * An unparseable input keeps the old rendering: `toLocaleString` answers
 * `"Invalid Date"` there, while `Intl.DateTimeFormat.format` throws a
 * `RangeError`, and a formatting change must not turn a bad timestamp in one
 * row into a blank page.
 */
export function formatDateTime(
  value: string | number | Date,
  locale: Locale = activeLocale(),
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(d);
  return new Intl.DateTimeFormat(locale, DATE_TIME_FIELDS).format(d);
}

/**
 * Grouped whole number in the app's locale, replacing `n.toLocaleString()`.
 *
 * Grouping stays on: these are byte counts and lifetime attempt counts where
 * the magnitude is the point of the column.
 */
export function formatCount(n: number, locale: Locale = activeLocale()): string {
  if (!Number.isFinite(n)) return String(n);
  return new Intl.NumberFormat(locale).format(n);
}

/**
 * Short calendar date in the app's locale, replacing
 * `d.toLocaleDateString(undefined, { year, month: "short", day: "2-digit" })`.
 *
 * The dash for an unparseable input is the one the tenants list already
 * rendered.
 */
export function formatShortDate(
  value: string | number | Date,
  locale: Locale = activeLocale(),
  dash = "—",
): string {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return dash;
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch {
    return dash;
  }
}

/**
 * Fixed-decimal number in the app's locale, replacing `toFixed(n)`.
 *
 * Grouping is off and the digit count is exact, so the only thing that moves
 * against `toFixed` is the decimal separator. The one caller prefixes its own
 * `$`: the currency of an entry is not decided here.
 */
export function formatFixed(
  n: number,
  fractionDigits: number,
  locale: Locale = activeLocale(),
): string {
  if (!Number.isFinite(n)) return n.toFixed(fractionDigits);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: false,
  }).format(n);
}

/**
 * B-GTCC-FMT6 — collation for operator-visible text.
 *
 * `String.prototype.localeCompare(other)` with no locale argument collates in
 * the machine's locale, the same fault the formatters above fix for numbers and
 * dates. It is only visible where the sorted strings are human text: measured
 * over the three group-C call sites, the two that sort machine identifiers
 * (dotted feature keys, database table names) collate identically in de, en, fr
 * and ar, and were left alone. The tenant picker sorts display names an operator
 * typed, and there Arabic collation puts Arabic-script names first while the
 * other three leave them last — a German workstation therefore ordered an Arabic
 * admin's tenant list the German way.
 *
 * No `numeric` and no `sensitivity`: those are `localeCompare`'s own defaults,
 * and this change is a locale fix, not a re-ordering. (`numeric: true` would put
 * "Site 2" before "Site 10" — a real improvement, but one that changes the order
 * in every locale including the one the operator has now, so it is a separate
 * decision and not this package's.)
 *
 * The collators are cached per locale because this runs inside a sort
 * comparator, where a fresh `Intl.Collator` per comparison is the expensive way
 * to do it.
 */
const COLLATORS = new Map<string, Intl.Collator>();

export function compareText(a: string, b: string, locale: Locale = activeLocale()): number {
  let collator = COLLATORS.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale);
    COLLATORS.set(locale, collator);
  }
  return collator.compare(a, b);
}

/**
 * B-GTCC-FMT8-A — the three `dateStyle`/`timeStyle` shapes the rest of the
 * admin pages build inline, plus the language-name lookup.
 *
 * These eight call sites were already locale-true: each one built its own
 * `new Intl.DateTimeFormat(locale, …)` from the catalogue locale, so none of
 * them rendered the machine's locale the way the FMT5 sites did. What they
 * repeat is the option shape, three times over in three variants, once per
 * page. Measured on the branch: `dateStyle: "medium"` with `timeStyle:
 * "medium"` once, with `timeStyle: "short"` three times, and `dateStyle:
 * "short"` with `timeStyle: "short"` twice.
 *
 * The helpers below reproduce those shapes exactly, so the rendered text does
 * not move for any locale. Unlike `formatDateTime` they take a `Date` and hand
 * back a formatter-shaped string only; the callers that need a reusable
 * formatter for a table body keep calling per row, which is what they already
 * did — `Intl` caches its own format objects.
 */

/** `dateStyle: "medium"`, `timeStyle: "medium"` — the audit log's timestamp. */
export function formatDateTimeMedium(
  value: string | number | Date,
  locale: Locale = activeLocale(),
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(d);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(d);
}

/** `dateStyle: "medium"`, `timeStyle: "short"` — email delivery, hardware, members. */
export function formatDateTimeMediumShort(
  value: string | number | Date,
  locale: Locale = activeLocale(),
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(d);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

/** `dateStyle: "short"`, `timeStyle: "short"` — the two tenant cards. */
export function formatDateTimeShortShort(
  value: string | number | Date,
  locale: Locale = activeLocale(),
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(d);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

/**
 * Language name in the app's locale, replacing an inline `Intl.DisplayNames`.
 *
 * The `try`/`catch` and the fall back to the raw code are the analytics
 * label's own behaviour, kept: `Intl.DisplayNames` throws on a locale a runtime
 * does not carry, and an analytics table must not go blank over a language tag
 * it cannot name.
 */
export function localeDisplayName(code: string, locale: Locale = activeLocale()): string {
  try {
    return new Intl.DisplayNames([locale], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * A-ADMIN-SAFE-TIMESTAMP1 / A-GTCC-SHARED-FORMAT-CONSUMER1 — the crash-safe
 * wrapper for every place in the admin app that turns a SERVER timestamp into
 * text, and its neutral dash.
 *
 * Both used to be restated here byte for byte, because the apps do not import
 * across their boundary. They now live in `@genius-tracker/shared-format`
 * (#2331), shared with the web app, and are re-exported so every existing
 * import from `./locale-format.js` keeps working unchanged — the same pattern
 * as `apps/web/src/lib/format.ts`. `locale-format.shared.test.ts` pins that
 * this stays a re-export and does not grow a copy again.
 */
export {
  UNREADABLE_TIMESTAMP_PLACEHOLDER,
  formatTimestampSafe,
} from "@genius-tracker/shared-format";
